import { useEffect, useMemo, useState } from 'react';

import { dayKey, fullDate, sprintYearPages } from '@app/utils';
import { SprintEntity, useListSprints } from '@data';

export interface SprintPagingFilters {
  /** Both bounds, as yyyy-mm-dd. The range only applies once each is set. */
  from?: string;
  to?: string;
  /** Whether the whole history is listed rather than the one sprint. A date range lists its own. */
  showAllSprints?: boolean;
}

export interface SprintPaging {
  /** The raw sprint query, so a caller can tell "still loading" from "loaded and empty". */
  sprints?: { items: SprintEntity[]; count: number };
  /** `sprints` is still the previous window, kept while the new one loads (see useListSprints). A
   * caller reading the list to decide what exists has to wait; one just rendering it should not. */
  sprintsArePrevious: boolean;
  /** The sprint the infinite list seeds from: the newest on the page. */
  baseSprint?: SprintEntity;
  /** The sprints shown, newest first; each gets its own section and request (see useSprintPages). */
  listSprints: SprintEntity[];
  currentSprint?: SprintEntity;
  yearPages: ReturnType<typeof sprintYearPages>;
  pageCount: number;
  currentPage: number;
  setPage: (page: number) => void;
  /** One bound filled and not the other: the range is not applied, and the empty field is flagged. */
  fromMissing: boolean;
  toMissing: boolean;
  /** Whether the history is on the page, so the year pager can be hidden along with it. */
  showsAllSprints: boolean;
}

/** While a date range is set the live sprint is not prepended: the window is the answer. */
export const useSprintPaging = ({
  from = '',
  to = '',
  showAllSprints = false,
}: SprintPagingFilters = {}): SprintPaging => {
  const rangeActive = Boolean(from && to);
  const [page, setPage] = useState(0);

  // A different window is a different set of pages, so start again at the most recent.
  useEffect(() => {
    setPage(0);
  }, [from, to, showAllSprints]);

  const rangeForSprints = useMemo(() => {
    if (rangeActive) {
      return { from: new Date(from), to: new Date(`${to}T23:59:59.999`), limit: 500 };
    }
    return { to: new Date(), limit: 500 };
  }, [rangeActive, from, to]);

  const { data: sprints, isPlaceholderData: sprintsArePrevious } = useListSprints(rangeForSprints);

  // Strictly the sprint running today: getCurrentSprint's fallback would head a finished sprint as live.
  const currentSprint = useMemo(() => {
    const today = dayKey(new Date());
    // A day both an ending and a starting sprint cover belongs to the one that just started, and the
    // query pins no order, so picking the first match would leave it to Postgres.
    return (sprints?.items ?? [])
      .filter(sprint => fullDate(sprint.startDate) <= today && today <= fullDate(sprint.endDate))
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0];
  }, [sprints]);

  // A range's sprints are all finished, so honouring the one-sprint view would answer with the wrong one.
  const showsAllSprints = showAllSprints || rangeActive;

  // Set apart by identity, not by date: a sprint ending the day the live one starts fell out of both.
  const pastSprints = useMemo(() => {
    const all = (sprints?.items ?? [])
      .slice()
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    if (rangeActive) return all;
    return all.filter(sprint => sprint.id !== currentSprint?.id);
  }, [sprints, currentSprint, rangeActive]);

  // One page per calendar year, so a page never straddles New Year (see sprintYearPages).
  const yearPages = useMemo(() => sprintYearPages(pastSprints), [pastSprints]);
  const pageCount = Math.max(1, yearPages.length);
  const currentPage = Math.min(page, pageCount - 1);
  const pageSprints = useMemo(() => yearPages[currentPage]?.sprints ?? [], [yearPages, currentPage]);

  // The live sprint belongs to no year page, so it heads the first rather than repeating under each.
  const listSprints = useMemo(() => {
    // The default view is the sprint being closed, the newest finished one: the live one has nothing to report.
    if (!showsAllSprints) {
      const onlySprint = pastSprints[0] ?? currentSprint;
      return onlySprint ? [onlySprint] : [];
    }
    const showsCurrent = !rangeActive && currentPage === 0 && currentSprint;
    return showsCurrent ? [currentSprint, ...pageSprints] : pageSprints;
  }, [showsAllSprints, rangeActive, currentPage, currentSprint, pastSprints, pageSprints]);

  return {
    sprints,
    sprintsArePrevious,
    baseSprint: listSprints[0],
    listSprints,
    currentSprint,
    yearPages,
    pageCount,
    currentPage,
    setPage,
    fromMissing: Boolean(to) && !from,
    toMissing: Boolean(from) && !to,
    showsAllSprints,
  };
};
